import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  input,
  Input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { resolveProjectStatusIds } from '@app/helpers/project-state.helper';
import type { UserAccount } from '@app/models/account.model';
import { ProjectAdminResponse } from '@app/models/admin.model';
import type { PaginatedData } from '@app/models/shared.models';
import { GetLocalizedPipe } from '@app/pipes/get-localized.pipe';
import { ContactService } from '@app/services/admin/contact.service';
import { GenericProjectStore } from '@app/stores/projects/generic-project.store';
import { CardComponent } from '@app/ui-components/card/card.component';
import { ValidatedDatePickerComponent } from '@app/ui-components/validated-date-picker/validated-date-picker.component';
import { ValidatedDropdownComponent } from '@app/ui-components/validated-dropdown/validated-dropdown.component';
import { TranslateModule } from '@ngx-translate/core';
import { finalize, map } from 'rxjs';
import type { AdminProjectIdeaFormGroup } from './admin-project-idea.form';

type ContactOption = { id: string; label: string };

@Component({
  selector: 'app-project-admin-idea-step',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    CardComponent,
    ValidatedDropdownComponent,
    ValidatedDatePickerComponent,
    GetLocalizedPipe,
  ],
  templateUrl: './project-admin-idea-step.component.html',
  styleUrl: './project-admin-idea-step.component.scss',
})
export class ProjectAdminIdeaStepComponent implements OnInit, OnChanges {
  genericProjectStore = inject(GenericProjectStore);
  private readonly contactService = inject(ContactService);
  private readonly destroyRef = inject(DestroyRef);

  @Input({ required: true }) form!: FormGroup<AdminProjectIdeaFormGroup>;
  @Input() project: ProjectAdminResponse | null = null;

  loading = input(false);

  readonly contactOptions = signal<ContactOption[]>([]);
  readonly loadingContacts = signal(false);

  canEditCollectionDates(): boolean {
    const statusIds = resolveProjectStatusIds(
      this.genericProjectStore.projectStatusEntities(),
    );
    return (this.project?.state ?? statusIds.Idea) <= statusIds.Publishable;
  }

  ngOnInit(): void {
    this.loadContacts();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['project'] && this.project) {
      const project = this.project;
      this.form.patchValue({
        state: project.state ?? null,
        foundingMode: project.foundingMode ?? null,
        investmentClassification: project.classification?.id ?? null,
        loanTerm: project.loanTerm ?? null,
        loanTypePaymentStart: project.loanTypePaymentStart,
        startCollectionDate: project.startCollectionDate ?? null,
        endCollectionDate: project.endCollectionDate ?? null,
        loanTypePaymentEnd: project.loanTypePaymentEnd,
        contactId: project.contactUserId ?? null,
      });
    }
  }

  private loadContacts(): void {
    this.loadingContacts.set(true);
    this.contactService
      .getContacts()
      .pipe(
        map((response: UserAccount[] | PaginatedData<UserAccount>) =>
          Array.isArray(response) ? response : (response?.data ?? []),
        ),
        map((users) =>
          users.map(
            (user) =>
              ({
                id: user.id,
                label: [
                  user.personalData?.firstName,
                  user.personalData?.lastName,
                ]
                  .filter(Boolean)
                  .join(' ')
                  .trim()
                  ? `${user.personalData?.firstName ?? ''} ${
                      user.personalData?.lastName ?? ''
                    }`.trim() + ` (${user.email ?? user.userName ?? ''})`
                  : (user.email ?? user.userName ?? ''),
              }) as ContactOption,
          ),
        ),
        finalize(() => this.loadingContacts.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (opts) => this.contactOptions.set(opts),
        error: () => this.contactOptions.set([]),
      });
  }
}
